import { action, computed, observable, runInAction } from 'mobx'
import { $RootStore } from '../../src/stores/RootStore'
import { ActivityStatus } from '../../graphQL/generated/enums'
import { sortBookingsByDate, todaysBookingsFilter } from '../lib/BookingFiltersHelper'

export const defaultNumbers: Omit<HomepageBookings, 'bookings' | 'externalBookings'> = {
  instants: 0,
  privateRequests: 0,
  requests: 0,
  cancel: 0,
  complete: 0,
  outstandingPayments: 0,
  outstandingConsultationForms: 0,
  chats: 0,
  reschedule: 0,
}

export class BaseHomeScreenViewModel {
  store: $RootStore
  @observable accessor activities: Activity[] = []

  constructor(store: $RootStore) {
    this.store = store
  }

  loadData = async () => {
    this.fetchData()
  }

  fetchData = async () => {
    const { statisticsStore } = this.store
    runInAction(() => {
      statisticsStore.selectedTime = 7
    })

    await Promise.all([
      this.fetchBookings(),
      statisticsStore.getHomeStats(),
      this.fetchActivities(),
      this.fetchWebsiteData(),
    ])
  }

  fetchActivities = async () => {
    const response = await this.store.communication.requester.activities()

    if (response) {
      runInAction(() => {
        this.activities = response.activities
      })
    }
  }

  updateActivityAndRefresh = async (_id: string, status: ActivityStatus) => {
    const response = await this.store.communication.requester.updateActivityStatus({
      _id,
      status,
    })

    if (response) {
      runInAction(() => {
        this.activities = response.updateActivityStatus
      })
    }
  }

  onHideActivity = (activity: Activity) => {
    this.updateActivityAndRefresh(activity._id, ActivityStatus.Hidden)
  }

  fetchBookings = async () => {
    const { bookingStore, uiStore } = this.store

    const updatedNumbers = await bookingStore.getBookingsForHomepage_api()

    if (updatedNumbers) {
      runInAction(() => {
        uiStore.todoNumbers = updatedNumbers
      })
    }
  }

  fetchWebsiteData = async () => {
    const { webPageStore } = this.store
    await webPageStore.fetchItems_api()
  }

  // Filter booking by status and group by date
  @computed get bookings() {
    const { items } = this.store.bookingStore
    const { items: externalItems } = this.store.externalBookingStore

    return [
      ...items,
      ...externalItems,
    ].sort(sortBookingsByDate).filter(todaysBookingsFilter)
  }

  @computed get hasBookings() {
    const { items } = this.store.bookingStore
    const { items: externalItems } = this.store.externalBookingStore
    return items.length + externalItems.length > 5
  }

  @computed get bookingsLoading() {
    const { bookingStore, externalBookingStore } = this.store
    return bookingStore.loading || externalBookingStore.loading
  }

  @computed get inboxLoading() {
    const { webPageStore } = this.store
    return webPageStore.loading
  }

  @computed get statsLoading() {
    const { statisticsStore } = this.store
    return statisticsStore.loading
  }

  @computed get showReferralPrompt() {
    const { data: { referralCode }, daysSinceSignUp } = this.store.userStore
    return daysSinceSignUp >= 14 && (referralCode?.length || 0) === 0
  }

  @computed get toolForMigration() {
    return this.store.uiStore.migrationTool
  }

  @action.bound dismissToolForMigration = () => {
    this.store.uiStore.migrationTool = undefined
    this.store.uiStore.syncInStorage()
  }

  @action.bound registerInterestInMigration = () => {
    const migrationOnboardingData = {
      question: 'splash_tools',
      answers: ['true'],
    }
    this.store.communication.requester.submitOnboardingAnswers({
      data: [migrationOnboardingData],
    })
    this.store.uiStore.migrationTool = undefined
    this.store.uiStore.showMessage({
      title: 'Let\'s get you set up!',
      body: 'One of our account managers will be in touch shortly to help you move your data across to Solo.',
    })
  }

  @computed get isSaasUser() {
    return this.store.authStore.isSaasUser
  }

  @computed get isReceptionSession() {
    return this.store.authStore.isReceptionSession
  }

  @computed get isWebsiteOffline() {
    const { webPageStore, userStore } = this.store
    const { saasSettings } = userStore.data.professionalProfile
    const homePage = webPageStore.webPageByPath('home')
    return this.isSaasUser && homePage && !saasSettings.isPublic
  }

  @computed get isWebsiteDraftUrl() {
    return this.store.userStore.data.professionalProfile.saasProfileUrl.startsWith('draft-')
  }

  @computed get todoNumbers() {
    return this.store.uiStore.todoNumbers
  }

  // Implement this in the child class

  @action.bound showInstantWaiting = () => {
    // Implement this in the child class
  }

  @action.bound showPrivateWaiting = () => {
    // Implement this in the child class
  }

  @action.bound showPrivateCancelRequested = () => {
    // Implement this in the child class
  }

  @action.bound showPrivateToComplete = () => {
    // Implement this in the child class
  }

  @action.bound showRequestWaiting = () => {
    // Implement this in the child class
  }

  @action.bound showReschedulesRequested = () => {
    // Implement this in the child class
  }

  @action.bound showOutstandingPayments = () => {
    // Implement this in the child class
  }

  @action.bound showOutstandingConsultationForms = () => {
    // Implement this in the child class
  }

  @action.bound showUnreadChat = () => {
    // Implement this in the child class
  }

  @action.bound goToReferralPrompt = () => {
    // Implement this in the child class
  }

  @action.bound goLiveWebsite = () => {
    // Implement this in the child class
  }
}
